import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  catchError,
  exhaustMap,
  from,
  map,
  mergeMap,
  of,
  startWith,
  switchMap,
  takeUntil
} from 'rxjs';
import { AuthActions } from '@components/auth/auth.actions';
import { DnaSkinFaceScanActionType } from '@interfaces/schema';
import { ReportSkinHealthGuidelineTakePictureActions } from './take-picture.actions';
import { selectReportSkinHealthGuidelineTakePictureImage } from './take-picture.selectors';
import { ReportSkinHealthGuidelineTakePictureService } from './take-picture.service';

/**
 * Report Skin Health Guideline Take Picture Effects.
 *
 * @exports
 * @class ReportSkinHealthGuidelineTakePictureEffects
 */
@Injectable()
export class ReportSkinHealthGuidelineTakePictureEffects extends ReportSkinHealthGuidelineTakePictureService {
  private readonly actions = inject(Actions);
  private readonly store = inject(Store);

  readonly initiateSubscription = createEffect(
    () =>
      this.actions.pipe(
        ofType(
          ReportSkinHealthGuidelineTakePictureActions.initiateUploadImageSubscription
        ),
        // exhaustMap ensures only one subscription is active at a time.
        // If a new initiate action comes before the previous one completes,
        // the new one is ignored. Use switchMap if you want to cancel the previous
        // and start a new one. For a single active subscription, exhaustMap is good.
        exhaustMap(() =>
          this.imageUploadProcess().pipe(
            mergeMap((response) => {
              let actionsToDispatch: Array<any> = [
                ReportSkinHealthGuidelineTakePictureActions.uploadImageSubscriptionDataReceived(
                  {
                    data: response
                  }
                )
              ];

              // replace the latest face scan process id in session storage
              this.session.store(
                this.session.keys.idLatestFaceScanProcess,
                response.data?.id
              );

              if (
                response.action === DnaSkinFaceScanActionType.Completed ||
                response.action === DnaSkinFaceScanActionType.Failed
              ) {
                actionsToDispatch.push(
                  ReportSkinHealthGuidelineTakePictureActions.setLoading({
                    loading: false
                  })
                );
              }
              return from(actionsToDispatch);
            }),
            // Handle errors from the subscription stream
            catchError((error) => {
              // eslint-disable-next-line no-console
              console.error(
                '[initiateSubscription] GraphQL Subscription Error:',
                error
              );
              return of(
                ReportSkinHealthGuidelineTakePictureActions.setLoading({
                  loading: false
                })
              );
            }),
            // takeUntil stops the subscription stream when 'stopUploadImageSubscription' is dispatched
            takeUntil(
              this.actions.pipe(
                ofType(
                  ReportSkinHealthGuidelineTakePictureActions.stopUploadImageSubscription
                )
              )
            ),
            startWith(
              ReportSkinHealthGuidelineTakePictureActions.setLoading({
                loading: true
              })
            )
          )
        )
      ),
    { dispatch: true }
  );

  readonly onAuthLogout_SwitchProfile = createEffect(
    () => {
      return this.actions.pipe(
        ofType(AuthActions.logout, AuthActions.switchProfile),
        map(() => ReportSkinHealthGuidelineTakePictureActions.reset())
      );
    },
    { dispatch: true }
  );

  readonly onUploadImage = createEffect(
    () => {
      return this.actions.pipe(
        ofType(ReportSkinHealthGuidelineTakePictureActions.uploadImage),
        concatLatestFrom(() => [
          this.store.select(selectReportSkinHealthGuidelineTakePictureImage)
        ]),
        switchMap(([, image]) => {
          const imageFile = this.base64ToBlob(image ?? '');
          if (!imageFile) {
            return of(
              ReportSkinHealthGuidelineTakePictureActions.setLoading({
                loading: false
              })
            );
          }
          return this.uploadPicture(imageFile).pipe(
            mergeMap(() => {
              return of(
                ReportSkinHealthGuidelineTakePictureActions.setLoading({
                  loading: false
                })
              );
            }),
            catchError(() => {
              return of(
                ReportSkinHealthGuidelineTakePictureActions.setLoading({
                  loading: false
                })
              );
            }),
            startWith(
              ReportSkinHealthGuidelineTakePictureActions.setLoading({
                loading: true
              })
            )
          );
        })
      );
    },
    { dispatch: true }
  );
}
